import { mutateResourceMeta } from '../../resource-detail/utils/mutateResourceMeta.js';
import { resourceQueryPredicates } from '../queries/resourceQueryPredicates.js';
import { useQueryClient } from '@tanstack/react-query';
import { resourceMatchesQueryFilters } from './resourceMatchesQueryFilters.js';
import { useOptimisticallyUpdateQueriesWithPredicate, useOptimisticallyUpdateInfiniteQueriesWithPredicate, upsertItemInArrayById } from '@fabric-app/queries/utilities';
import { isTruthy } from '@fabric-app/utils/guards';
import { addPendingMutation } from '@fabric-app/queries/pending';
import { useQueryCacheSpaceHelpers } from '../../spaces/utils/useQueryCacheSpaceHelpers.js';

const useQueryCacheResourceHelpers = () => {
  const queryClient = useQueryClient();
  const optimisticallyUpdateQueries = useOptimisticallyUpdateQueriesWithPredicate();
  const optimisticallyUpdateInfiniteQueries = useOptimisticallyUpdateInfiniteQueriesWithPredicate();
  const { updateCachedSpace } = useQueryCacheSpaceHelpers();
  return {
    updateCachedResource: (nextResource, options) => {
      queryClient.cancelQueries({
        predicate: (q) => resourceQueryPredicates.resourceListAny(q) || resourceQueryPredicates.resourceDetail(q, nextResource.id),
        type: "active"
      });
      const optimisticallyUpdateResourceListQueries = optimisticallyUpdateInfiniteQueries({
        predicate: resourceQueryPredicates.resourceListAny,
        pageUpdater: (page) => ({
          ...page,
          resources: upsertItemInArrayById(page.resources, nextResource, {
            createIfNotFound: false,
            updateFn: options?.resourceUpdateFn
          })
        })
      });
      const optimisticallyUpdatedResourceDetailQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, nextResource.id), (resource) => resource && {
        ...resource,
        ...nextResource
      });
      const cacheUpdateSpaces = updateCachedSpace(nextResource);
      return {
        optimisticallyUpdateResourceListQueries,
        optimisticallyUpdatedResourceDetailQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateResourceListQueries.resetQueriesData();
          optimisticallyUpdatedResourceDetailQueries.resetQueriesData();
          cacheUpdateSpaces.resetCacheToPreOptimisticState();
        },
        invalidateQueries: () => {
          optimisticallyUpdateResourceListQueries.invalidateQueries();
          optimisticallyUpdatedResourceDetailQueries.invalidateQueries();
          cacheUpdateSpaces.invalidateQueries();
        }
      };
    },
    deleteCachedResources: (resourceIds, options) => {
      const listPredicate = options?.listPredicate || resourceQueryPredicates.resourceListAny;
      queryClient.cancelQueries({
        predicate: (q) => listPredicate(q) || resourceQueryPredicates.resourceMultiple(q, resourceIds),
        type: "active"
      });
      const deletedResources = [];
      const deletedResourceIds = new Set(resourceIds);
      const queries = queryClient.getQueriesData({
        predicate: listPredicate,
        type: "active"
      });
      const tree = /* @__PURE__ */ new Map();
      for (const [_, data] of queries) {
        if (!data) {
          continue;
        }
        for (const resource of data.pages.flatMap((page) => page.resources)) {
          if (!resource.parent?.id) {
            continue;
          }
          if (!tree.has(resource.parent.id)) {
            tree.set(resource.parent.id, []);
          }
          tree.get(resource.parent.id).push(resource);
        }
      }
      const stack = [
        ...resourceIds
      ];
      while (stack.length > 0) {
        const resourceId = stack.pop();
        if (!tree.has(resourceId)) {
          continue;
        }
        for (const resource of tree.get(resourceId)) {
          if (!deletedResourceIds.has(resource.id)) stack.push(resource.id);
          deletedResourceIds.add(resource.id);
        }
      }
      const optimisticallyUpdateFilterAndSearchAllQueries = optimisticallyUpdateInfiniteQueries({
        predicate: listPredicate,
        pageUpdater: (page) => {
          const updatedResults = page.resources.filter((r) => {
            if (deletedResourceIds.has(r.id)) {
              deletedResources.push(r);
              return false;
            }
            return true;
          });
          const difference = page.resources.length - updatedResults.length;
          return {
            ...page,
            resources: updatedResults,
            // we make sure to change the total because this affects how many skeletons are shown
            total: Math.max(0, (page.total ?? 0) - difference)
          };
        }
      });
      const optimisticallyUpdateDirectQueries = optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceMultiple(q, Array.from(deletedResourceIds)), (resource) => resource && mutateResourceMeta(resource, {
        isDeleting: true
      }));
      return {
        deletedResources: [
          ...deletedResources
        ].filter((resource, index, self) => self.findIndex((r) => r.id === resource.id) === index),
        optimisticallyUpdateFilterAndSearchAllQueries,
        optimisticallyUpdateDirectQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticallyUpdateFilterAndSearchAllQueries.resetQueriesData();
          optimisticallyUpdateDirectQueries.resetQueriesData();
        },
        invalidateQueries: () => {
          optimisticallyUpdateFilterAndSearchAllQueries.invalidateQueries();
          optimisticallyUpdateDirectQueries.invalidateQueries();
        }
      };
    },
    addNewResourcesToCache: (newResources) => {
      const resourcesByParent = newResources.filter((resource) => !!resource.parent?.id).reduce((acc, resource) => {
        if (!acc[resource.parent.id]) {
          acc[resource.parent.id] = [];
        }
        acc[resource.parent.id].push(resource);
        return acc;
      }, {});
      Object.keys(resourcesByParent).forEach((parentResourceId) => {
        queryClient.cancelQueries({
          predicate: (q) => resourceQueryPredicates.resourceListAnyByParentId(q, parentResourceId),
          type: "active"
        });
      });
      const optimisticUpdates = Object.entries(resourcesByParent).map(([parentResourceId, resources]) => {
        const optimisticallyUpdateFilterAndSearchQueries = optimisticallyUpdateInfiniteQueries({
          predicate: (q) => resourceQueryPredicates.resourceListAnyByParentId(q, parentResourceId),
          pageUpdater: (page, index, key) => {
            if (index > 0) return page;
            const validResources = resources.filter((r) => resourceMatchesQueryFilters(r, key));
            const nextResources = [
              ...validResources,
              ...page.resources
            ];
            const diff = nextResources.length - page.resources.length;
            return {
              ...page,
              resources: nextResources,
              total: page.total + diff
            };
          }
        });
        return {
          parentResourceId,
          optimisticallyUpdateFilterAndSearchQueries
        };
      });
      const optimisticUpdateDirectQueries = newResources.map((resource) => optimisticallyUpdateQueries((q) => resourceQueryPredicates.resourceDetail(q, resource.id), () => resource));
      const allQueryKeys = queryClient.getQueryCache().findAll({
        predicate: (query) => resourceQueryPredicates.resourceMultiple(query, newResources.map((r) => r.id)) || resourceQueryPredicates.resourceListAnyByMultipleParentIds(query, newResources.map((r) => r.parent?.id).filter(isTruthy)),
        type: "active"
      }).map((query) => query.queryKey);
      const clearPendingState = addPendingMutation(allQueryKeys);
      return {
        optimisticUpdates,
        optimisticUpdateDirectQueries,
        resetCacheToPreOptimisticState: () => {
          optimisticUpdates.forEach(({ optimisticallyUpdateFilterAndSearchQueries }) => optimisticallyUpdateFilterAndSearchQueries.resetQueriesData());
          optimisticUpdateDirectQueries.forEach((optimisticUpdateDirectQuery) => optimisticUpdateDirectQuery.resetQueriesData());
        },
        invalidateQueries: () => {
          optimisticUpdates.forEach(({ optimisticallyUpdateFilterAndSearchQueries }) => optimisticallyUpdateFilterAndSearchQueries.invalidateQueries());
          optimisticUpdateDirectQueries.forEach((optimisticUpdateDirectQuery) => optimisticUpdateDirectQuery.invalidateQueries());
        },
        clearPendingState
      };
    }
  };
};

export { useQueryCacheResourceHelpers };
//# sourceMappingURL=useQueryCacheResourceHelpers.js.map
